import _ from 'lodash'
import Heading from '@tiptap/extension-heading'
import Blockquote from '@tiptap/extension-blockquote'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { StarterKit } from '@tiptap/starter-kit'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Editor } from '@tiptap/react'
import { EditorState, Transaction } from 'prosemirror-state'
import { common, createLowlight } from 'lowlight'
import { Markdown } from '@tiptap/markdown'
import { StyleType } from '../../../../utils/markdown'
import { ActiveCellHighlight } from './TableInteractions/activeCellHighlightExtension'

const lowlight = createLowlight(common)

export const getEditorExtensions = () => {
  return [
    StarterKit.configure({
      codeBlock: false,
      heading: false,
      blockquote: false,
      horizontalRule: false,
      paragraph: false,
    }),
    CustomParagraph,
    CustomHeading,
    CustomBlockquote,
    Link,
    Image.configure({
      inline: true,
    }),
    CustomCodeBlock.configure({
      lowlight,
    }),
    CustomHorizontalRule,
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Table,
    TableCell,
    TableHeader,
    TableRow,
    ActiveCellHighlight,
    Markdown.configure({
      markedOptions: {
        breaks: true,
      },
    }),
  ]
}

// Draftエディタが空段落の代わりに使う目印。getMarkdown/parseMarkdownでラウンドトリップする。
const EMPTY_PARAGRAPH_MARKER = '\xA0'

// Draftエディタに適用する前のMarkdown前処理。
// Markdownソースエディタ形式（\n 区切り）では \n\n がブランク行を表すが、
// marked.js のヘッダートークンが \n\n を消費するため空行が失われる。
// \xA0 の明示的な空段落マーカーに変換して空行を保持する。
// すでに \xA0 マーカーが含まれる（Draftエディタ形式の）場合は変換不要。
export const ensureExplicitEmptyParagraphs = (md: string): string => {
  if (md.includes(EMPTY_PARAGRAPH_MARKER)) return md
  return md.replace(/\n\n/g, `\n\n${EMPTY_PARAGRAPH_MARKER}\n\n`)
}

// Markdownソース編集用の最小構成。装飾系を無効化してプレーンテキスト扱いにする。
// @tiptap/markdown は含めず、editor.getText() で生マークダウン文字列として取り出す。
export const getMarkdownSourceExtensions = () => {
  return [
    StarterKit.configure({
      blockquote: false,
      bold: false,
      bulletList: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      italic: false,
      link: false,
      listItem: false,
      listKeymap: false,
      orderedList: false,
      strike: false,
      underline: false,
    }),
  ]
}

// \xA0 マーカー行とその隣接する空行を1つの空行に畳み込む。
// Draftエディタの getMarkdown() 出力をプレビューやソースエディタに渡す前の正規化に使う。
export const collapseEmptyParagraphMarkers = (markdown: string): string => {
  const lines = markdown.split('\n')
  const toRemove = new Set<number>()
  lines.forEach((line, i) => {
    if (line !== EMPTY_PARAGRAPH_MARKER) return
    toRemove.add(i)
    if (i > 0 && lines[i - 1] === '') toRemove.add(i - 1)
    else if (i < lines.length - 1 && lines[i + 1] === '') toRemove.add(i + 1)
  })
  return lines.filter((_, i) => !toRemove.has(i)).join('\n')
}

// Markdown文字列を、Markdownソースエディタ用のdocノードに変換する。
// 改行ごとに段落を作ることで、getText({ blockSeparator: '\n' }) で元の文字列に戻せる。
export const markdownToSourceDoc = (markdown: string) => {
  const collapsed = collapseEmptyParagraphMarkers(markdown).split('\n')
  return {
    type: 'doc',
    content: collapsed.map(line =>
      line.length > 0
        ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
        : { type: 'paragraph' }
    ),
  }
}

// Markdownソースエディタから、生のMarkdown文字列を取り出す。
export const getMarkdownFromSourceEditor = (editor: Editor): string => {
  return editor.getText({ blockSeparator: '\n' })
}

const CustomParagraph = Paragraph.extend({
  renderMarkdown: (node, h, ctx) => {
    const content = Array.isArray(node.content) ? node.content : []
    if (content.length > 0) {
      return h.renderChildren(content)
    }
    // 先頭ノードの空段落は無視。それ以外は常に \xA0 マーカーを返して
    // getMarkdown→setContent のラウンドトリップを安定させる。
    if (!ctx?.previousNode) return ''
    return EMPTY_PARAGRAPH_MARKER
  },
})

const CustomCodeBlock = CodeBlockLowlight.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => this.editor.commands.toggleCodeBlock(),
    }
  },
  renderHTML({ HTMLAttributes }) {
    return ['pre', ['pre', ['code', HTMLAttributes, 0]]]
  },
})

const CustomBlockquote = Blockquote.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-.': () => this.editor.commands.toggleBlockquote(),
    }
  },
})

const CustomHeading = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-1': () => this.editor.commands.toggleHeading({ level: 1 }),
      'Mod-Shift-2': () => this.editor.commands.toggleHeading({ level: 2 }),
      'Mod-Shift-3': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Shift-4': () => this.editor.commands.toggleHeading({ level: 4 }),
      'Mod-Shift-5': () => this.editor.commands.toggleHeading({ level: 5 }),
      'Mod-Shift-6': () => this.editor.commands.toggleHeading({ level: 6 }),
    }
  },
})

const CustomHorizontalRule = HorizontalRule.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-h': () => this.editor.commands.setHorizontalRule(),
    }
  },
})

export const splitHardBreaks = ({
  tr,
  state,
}: {
  tr: Transaction
  state: EditorState
}): boolean => {
  const { from, to } = state.selection
  const hardBreakPositions: number[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'hardBreak') {
      hardBreakPositions.push(pos)
    }
  })

  for (let i = hardBreakPositions.length - 1; i >= 0; i--) {
    const pos = hardBreakPositions[i]
    tr.delete(pos, pos + 1)
    tr.split(pos)
  }

  return true
}

export const findHighLightItemsInRichText = (editor: Editor): string[] => {
  let items: string[] = []
  if (editor.isActive('heading', { level: 1 })) {
    items = [...items, StyleType.HEADER_ONE]
  }
  if (editor.isActive('heading', { level: 2 })) {
    items = [...items, StyleType.HEADER_TWO]
  }
  if (editor.isActive('heading', { level: 3 })) {
    items = [...items, StyleType.HEADER_THREE]
  }
  if (editor.isActive('heading', { level: 4 })) {
    items = [...items, StyleType.HEADER_FOUR]
  }
  if (editor.isActive('heading', { level: 5 })) {
    items = [...items, StyleType.HEADER_FIVE]
  }
  if (editor.isActive('heading', { level: 6 })) {
    items = [...items, StyleType.HEADER_SIX]
  }
  if (editor.isActive('bold')) items = [...items, StyleType.BOLD]
  if (editor.isActive('italic')) items = [...items, StyleType.ITALIC]
  if (editor.isActive('strike')) items = [...items, StyleType.STRIKETHROUGH]
  if (editor.isActive('code')) items = [...items, StyleType.CODE]
  if (editor.isActive('codeBlock')) items = [...items, StyleType.CODE_BLOCK]
  if (editor.isActive('blockquote')) items = [...items, StyleType.BLOCKQUOTE]
  if (editor.isActive('bulletList')) {
    items = [...items, StyleType.UNORDERED_LIST_ITEM]
  }
  if (editor.isActive('orderedList')) {
    items = [...items, StyleType.ORDERED_LIST_ITEM]
  }
  if (editor.isActive('horizontalRule')) {
    items = [...items, StyleType.HORIZONTAL_RULE]
  }
  if (editor.isActive('taskList')) items = [...items, StyleType.TASK_LIST]
  if (editor.isActive('table')) items = [...items, StyleType.TABLE]
  if (editor.isActive('image')) items = [...items, StyleType.FILE]
  return items
}

const CLIPBOARD_DATA_TYPE_HTML: string = 'text/html'
const HTML_ANCHOR_TAG_REGEX: string = '<a '

export const insertLinkFromClipboard = (
  clipboardData: DataTransfer,
  editor: Editor
): boolean => {
  if (
    !clipboardData ||
    clipboardData.types.indexOf(CLIPBOARD_DATA_TYPE_HTML) < 0
  ) {
    return false
  }

  const html: string = clipboardData.getData(CLIPBOARD_DATA_TYPE_HTML)
  if (
    _.isEmpty(html) ||
    !new RegExp(HTML_ANCHOR_TAG_REGEX).test(html.replaceAll('\r\n', ''))
  ) {
    return false
  }
  editor.commands.insertContent(html, {
    parseOptions: {
      preserveWhitespace: false,
    },
  })
  return true
}
